import { z } from 'zod';
/**
 * The published privacy policy's version, and the consent record that proves
 * agreement to it.
 *
 * IN CONTRACTS, FOR THE REASON THIS PACKAGE EXISTS (ADR-0002). Three deployment
 * units carry this string: the staff app shows it and sends it, the API
 * validates and stores it, and the manager console renders the page it names. A
 * copy in any of those three is a copy that will one day claim a user agreed to
 * a version of a document that was never published — which is the single defect
 * this record cannot survive, because its entire value is that it is true.
 *
 * DATE-SHAPED, NOT SEMVER. It sorts, it is what is printed at the top of the
 * published page, and a legal document does not have a patch release.
 */
export const PRIVACY_POLICY_VERSION = '2026-08-01';
/**
 * Every version ever published, oldest first.
 *
 * WHY THE API VALIDATES AGAINST A SET RATHER THAN AGAINST EQUALITY.
 *
 * There is no over-the-air channel — `docs/runbooks/mobile-release.md` is
 * explicit that a one-line JavaScript fix ships through the stores like a native
 * change. So on the day the policy is bumped there are handsets in the field
 * still running the previous binary and posting the previous version, and those
 * posts are CORRECT: the person agreed to the text they were actually shown.
 * Rejecting them would be rejecting valid consent and locking a salesperson out
 * mid-shift over a compliance write.
 *
 * A typo must still fail loudly, which is why this is a set and not
 * `z.string()`. NEVER REMOVE AN ENTRY — a version that has been consented to is
 * history, and history is what this record is for.
 */
export const KNOWN_POLICY_VERSIONS = ['2026-08-01'];
/**
 * Where the published documents live, relative to the manager console's origin.
 *
 * Declared here rather than in each client so the in-app links, the store
 * console fields and the router cannot drift apart. The paths are the
 * INDONESIAN ones, which are canonical — see `pages/legal/LegalLayout.tsx`.
 */
export const LEGAL_PATHS = {
    privacy: '/legal/privacy',
    terms: '/legal/terms',
    accountDeletion: '/legal/account-deletion',
    support: '/legal/support',
};
export const ConsentDecision = z.enum(['ACCEPTED', 'DECLINED']);
/**
 * `POST /auth/consent` — the UU 27/2022 consent record, and the evidence RSK-01
 * and TAD 4.6.1 both ask for.
 *
 * DECLINED IS RECORDED, not only accepted. A refusal is a withdrawal signal the
 * controller has an obligation to act on, and a refusal that lives only on the
 * phone of the person who refused is a signal nobody will ever see.
 *
 * `locale` is which language the disclosure was READ in, not the device locale.
 * The in-app disclosure is Indonesian only today; this field is what makes the
 * question "which text did they agree to?" answerable if that ever changes.
 *
 * `idempotencyKey`, as everywhere else in this API: the client retries when the
 * first attempt died offline, and a retry must not mint a second record.
 *
 * NO `device` FIELD, deliberately. A native session is already bound to a device
 * — `refresh_tokens` carries `deviceId`, `deviceName` and `platform` from login
 * — so the server can name the handset a consent arrived from without being
 * told. Restating it here would let a client claim a device it is not on, and
 * would make this module import `DeviceInfo` from `auth.js` while `auth.js`
 * imports `ConsentDecision` back: a dependency cycle, for a field the server
 * should not trust from the client anyway.
 */
export const RecordConsentRequest = z.object({
    policyVersion: z.enum(KNOWN_POLICY_VERSIONS),
    decision: ConsentDecision,
    /** The device clock at the moment of the tap. The server stamps its own too. */
    decidedAt: z.string().datetime(),
    locale: z.enum(['id', 'en']),
    idempotencyKey: z.string().min(16).max(128),
});
/** What the server holds, echoed back and surfaced on `GET /auth/profile`. */
export const ConsentRecord = z.object({
    policyVersion: z.string(),
    decision: ConsentDecision,
    decidedAt: z.string(),
    /** When the SERVER received it, which may be much later than `decidedAt`. */
    recordedAt: z.string(),
});
//# sourceMappingURL=legal.js.map